import React, { memo, ChangeEvent } from 'react';
import styled from 'styled-components';
import { SCAN_SEARCH_PLACEHOLDER } from '../config';
import SearchResultItem from './SearchResultItem';
import ScanResultBanner from './ScanResultBanner';
import ParticipantDetailCard from './ParticipantDetailCard';
import ScanCheckInButtons from './ScanCheckInButtons';
import type { ScanSearchResultItem, ScanParticipant } from '../types/types';
import { SCAN_RESULT_MESSAGE } from '../utils/utils';

type SearchPanelProps = {
	keyword: string;
	results: ScanSearchResultItem[];
	selectedBib: string | null;
	onKeywordChange: (keyword: string) => void;
	onSelect: (bibNumber: string) => void;
	onCompleteCheckIn: (participant: ScanParticipant) => void;
	onCancelSelect: () => void;
};

const IDENTITY_CONFIRM_TEXT = '본인 확인 — 참가자에게 생년월일을 물어 일치하는지 확인하세요';

// 직접 검색 모드 패널
const SearchPanel = memo<SearchPanelProps>(
	({ keyword, results, selectedBib, onKeywordChange, onSelect, onCompleteCheckIn, onCancelSelect }) => {
		const handleChange = (e: ChangeEvent<HTMLInputElement>) => onKeywordChange(e.target.value);
		const hasKeyword = keyword.trim().length > 0;

		return (
			<Wrapper>
				<SearchInput value={keyword} onChange={handleChange} placeholder={SCAN_SEARCH_PLACEHOLDER} />
				{/* 검색어 없음 안내 */}
				{!hasKeyword && <EmptyText>검색어를 입력하시면 대회 참가자 목록이 표시됩니다.</EmptyText>}
				{/* 검색 결과 목록 */}
				{hasKeyword && results.length === 0 && <EmptyText>일치하는 참가자가 없습니다.</EmptyText>}
				{hasKeyword &&
					results.map(item => {
						const isSelected = selectedBib === item.bibNumber;

						if (item.receiveStatus === 'RECEIVED') {
							return (
								<React.Fragment key={item.bibNumber}>
									<SearchResultItem item={item} selected={isSelected} onClick={onSelect} />
									{isSelected && (
										<SelectedBox>
											<ScanResultBanner variant="error" text={SCAN_RESULT_MESSAGE['ALREADY_RECEIVED']} />
											<ParticipantDetailCard
												participant={item.participant}
												tone="orange"
												footer={<ConfirmButton onClick={onCancelSelect}>확인</ConfirmButton>}
											/>
										</SelectedBox>
									)}
								</React.Fragment>
							);
						}

						return (
							<React.Fragment key={item.bibNumber}>
								<SearchResultItem item={item} selected={isSelected} onClick={onSelect} />
								{isSelected && (
									<SelectedBox>
										<ScanResultBanner variant="info" text={IDENTITY_CONFIRM_TEXT} />
										<ParticipantDetailCard
											participant={item.participant}
											tone="blue"
											footer={
												<ScanCheckInButtons
													onComplete={() => onCompleteCheckIn(item.participant)}
													onCancel={onCancelSelect}
												/>
											}
										/>
									</SelectedBox>
								)}
							</React.Fragment>
						);
					})}
			</Wrapper>
		);
	}
);

SearchPanel.displayName = 'SearchPanel';

export default SearchPanel;

const Wrapper = styled.div`
	padding-top: 24px;
`;
const SearchInput = styled.input`
	width: 100%;
	height: 56px;
	font-size: 15px;
	padding: 0 20px;
	margin-bottom: 24px;
	border-radius: 6px;
	border: 1px solid ${({ theme }) => theme.colors.lightGrayBorder};
	outline: none;
	transition: border-color 0.2s ease;

	&::placeholder {
		color: ${({ theme }) => theme.colors.lightGrayFontColor};
	}

	&:focus {
		border-color: ${({ theme }) => theme.colors.blackColor};
	}
`;
const EmptyText = styled.div`
	font-size: 14px;
	color: ${({ theme }) => theme.colors.lightGrayFontColor};
	padding: 8px 0;
`;
const SelectedBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding: 24px 0;
`;
const ConfirmButton = styled.button`
	width: 100%;
	height: 56px;
	font-size: 16px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	margin-top: auto;
	border-radius: 6px;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.deepGrayFontColor};
	background-color: ${({ theme }) => theme.colors.whiteColor};
	transition: 0.2s;

	&:hover {
		background-color: ${({ theme }) => theme.colors.lightGrayHoverColor};
	}
`;
